import React from 'react';
import {
  Award,
  CreditCard,
  DollarSign,
  Loader2,
  Plus,
  Trash2,
  TrendingUp,
} from 'lucide-react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';

const PricingTab = ({
  prices,
  setPrices,
  coupons,
  showCouponModal,
  setShowCouponModal,
  newCoupon,
  setNewCoupon,
  loading,
  creating,
  handleCreateCoupon,
  handleDeleteCoupon,
}) => {
  return (
    <div className="space-y-6">
      <Card className="border-emerald-200 bg-gradient-to-br from-[#eef8f2] via-white to-[#edf4f1] p-6 dark:border-emerald-500/15 dark:bg-gradient-to-br dark:from-[#162019] dark:via-[#121814] dark:to-[#101512]">
        <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-emerald-700 dark:text-emerald-300">
          Business Health flow
        </p>
        <h3 className="mt-3 text-2xl font-semibold text-slate-900 dark:text-white">
          Keep pricing and offers tied to durability, not just activity
        </h3>
        <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-gray-300">
          Use this tool to manage the pricing levers that support the mission over time. The goal is not to keep more offers alive than necessary, but to keep the business healthy and understandable.
        </p>
      </Card>

      <Card className="bg-[#1e1e24] border-[#2a2a32] p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-aqua" />
          Membership Pricing
        </h3>
        <div className="max-w-md">
          <div className="p-4 bg-[#16161a] rounded-xl">
            <h4 className="text-white font-medium mb-3">Standard Member</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Monthly</span>
                <div className="flex items-center gap-2">
                  <span className="text-white">$</span>
                  <Input type="number" value={prices.monthly} onChange={(e) => setPrices({ ...prices, monthly: e.target.value })} className="w-20 bg-[#1e1e24] border-[#2a2a32] text-white" />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Annual</span>
                <div className="flex items-center gap-2">
                  <span className="text-white">$</span>
                  <Input type="number" value={prices.annual} onChange={(e) => setPrices({ ...prices, annual: e.target.value })} className="w-20 bg-[#1e1e24] border-[#2a2a32] text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <Button className="mt-4 bg-aqua hover:bg-aqua-dark text-charcoal">Save Pricing Changes</Button>
      </Card>

      <Card className="bg-[#1e1e24] border-[#2a2a32] p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Award className="w-5 h-5 text-aqua" />
            Coupons & Discounts
          </h3>
          <Button onClick={() => setShowCouponModal(true)} className="bg-aqua hover:bg-aqua-dark text-charcoal gap-2">
            <Plus className="w-4 h-4" />
            Create Coupon
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 text-aqua animate-spin" />
          </div>
        ) : coupons.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No coupons created yet</p>
        ) : (
          <div className="space-y-3">
            {coupons.map(coupon => (
              <div key={coupon.coupon_id} className="p-4 bg-[#16161a] rounded-xl flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-3">
                    <Badge className="bg-aqua/10 text-aqua font-mono">{coupon.code}</Badge>
                    <span className="text-white font-medium">{coupon.title}</span>
                    {!coupon.is_active && <Badge className="bg-gray-500/10 text-gray-400">Inactive</Badge>}
                  </div>
                  <p className="text-gray-400 text-sm mt-1">
                    {coupon.discount_type === 'percentage' ? `${coupon.discount_value}% off` : `$${coupon.discount_value} off`}
                    {coupon.max_uses && ` • ${coupon.uses_count || 0}/${coupon.max_uses} uses`}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => handleDeleteCoupon(coupon.coupon_id)} className="text-red-400 hover:text-red-300">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {showCouponModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="bg-[#1e1e24] border-[#2a2a32] p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-white mb-4">Create New Coupon</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-gray-400 text-sm">Coupon Code *</label>
                  <Input
                    placeholder="WELCOME20"
                    value={newCoupon.code}
                    onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })}
                    className="bg-[#16161a] border-[#2a2a32] text-white mt-1 font-mono"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-sm">Title *</label>
                  <Input
                    placeholder="Welcome Discount"
                    value={newCoupon.title}
                    onChange={(e) => setNewCoupon({ ...newCoupon, title: e.target.value })}
                    className="bg-[#16161a] border-[#2a2a32] text-white mt-1"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-sm">Description</label>
                  <Textarea
                    placeholder="Special discount for new members"
                    value={newCoupon.description}
                    onChange={(e) => setNewCoupon({ ...newCoupon, description: e.target.value })}
                    className="bg-[#16161a] border-[#2a2a32] text-white mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-gray-400 text-sm">Discount Type</label>
                    <select
                      value={newCoupon.discount_type}
                      onChange={(e) => setNewCoupon({ ...newCoupon, discount_type: e.target.value })}
                      className="w-full bg-[#16161a] border border-[#2a2a32] text-white rounded-lg px-3 py-2 mt-1"
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount ($)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm">Value</label>
                    <Input
                      type="number"
                      value={newCoupon.discount_value}
                      onChange={(e) => setNewCoupon({ ...newCoupon, discount_value: Number(e.target.value) })}
                      className="bg-[#16161a] border-[#2a2a32] text-white mt-1"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-gray-400 text-sm">Max Uses (leave empty for unlimited)</label>
                  <Input
                    type="number"
                    placeholder="Unlimited"
                    value={newCoupon.max_uses || ''}
                    onChange={(e) => setNewCoupon({ ...newCoupon, max_uses: e.target.value ? Number(e.target.value) : null })}
                    className="bg-[#16161a] border-[#2a2a32] text-white mt-1"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button variant="outline" onClick={() => setShowCouponModal(false)} className="flex-1 border-[#2a2a32] text-white hover:bg-[#16161a]">
                    Cancel
                  </Button>
                  <Button onClick={handleCreateCoupon} disabled={creating} className="flex-1 bg-aqua hover:bg-aqua-dark text-charcoal">
                    {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Coupon'}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}
      </Card>

      <Card className="bg-[#1e1e24] border-[#2a2a32] p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-aqua" />
          Payment Integration
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="p-4 bg-[#16161a] rounded-xl border border-aqua/30">
            <div className="flex items-center justify-between mb-3">
              <span className="text-white font-medium">Stripe</span>
              <Badge className="bg-green-500/10 text-green-400">Connected</Badge>
            </div>
            <p className="text-gray-400 text-sm mb-3">Accept credit cards, Apple Pay, Google Pay</p>
            <p className="text-xs text-gray-500">Payments will be processed through Stripe checkout</p>
          </div>
          <div className="p-4 bg-[#16161a] rounded-xl border border-aqua/30">
            <div className="flex items-center justify-between mb-3">
              <span className="text-white font-medium">PayPal</span>
              <Badge className="bg-green-500/10 text-green-400">Connected</Badge>
            </div>
            <p className="text-gray-400 text-sm mb-3">Accept PayPal payments</p>
            <p className="text-xs text-gray-500">PayPal integration configured</p>
          </div>
        </div>
      </Card>

      <Card className="bg-[#1e1e24] border-[#2a2a32] p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-aqua" />
          Financial Analytics
        </h3>
        <p className="text-gray-400 text-sm mb-4">Connect payment processors to see real-time financial data</p>
        <div className="grid md:grid-cols-4 gap-4">
          <div className="p-4 bg-[#16161a] rounded-xl text-center">
            <p className="text-gray-400 text-sm">MRR</p>
            <p className="text-2xl font-bold text-white">--</p>
          </div>
          <div className="p-4 bg-[#16161a] rounded-xl text-center">
            <p className="text-gray-400 text-sm">ARR</p>
            <p className="text-2xl font-bold text-white">--</p>
          </div>
          <div className="p-4 bg-[#16161a] rounded-xl text-center">
            <p className="text-gray-400 text-sm">Avg Revenue/Member</p>
            <p className="text-2xl font-bold text-white">--</p>
          </div>
          <div className="p-4 bg-[#16161a] rounded-xl text-center">
            <p className="text-gray-400 text-sm">Lifetime Value</p>
            <p className="text-2xl font-bold text-white">--</p>
          </div>
        </div>
        <p className="text-gray-500 text-xs mt-4 text-center">Financial data will populate once payment integrations are connected</p>
      </Card>
    </div>
  );
};

export default PricingTab;
